import React, { useContext, useEffect, useState } from "react"
import { storageData } from "services/Services"
import { HouseholdChangeContext } from "./HouseholdChangeContext";
import { AddressChangeContext } from "components/StraightThroughAddressComp/AddressContext/AddressChangeContext";
import { useLocation } from "react-router-dom";
import monthsDOB from "../CustomInputs/StateObjects/Months";
import { InitialDriverHHM } from "./InitialDriverHHM";

export const DriverHHMContext = React.createContext()

const useModal = () => {
    const [modalTitle, setModalTitle] = useState("");
    const [modalMsg, setModalMsg] = useState("");
    const [showModal, setShowModal] = useState(false);

    const openModal = (title, message) => {
        setModalTitle(title);
        setModalMsg(message);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
    };

    return {
        modalTitle,
        modalMsg,
        showModal,
        openModal,
        closeModal,
    };
};

// Helper function to convert month name to month index
const monthNameToIndex = (monthName) => {
    const months = monthsDOB
    return months.indexOf(monthName);
};

// Retrieves the saved responses to display
const getSavedForm = (changeClass) => {
    if (changeClass?.driversHHMs?.drivers?.newDrivers?.length > 0) {
        return changeClass?.driversHHMs?.drivers?.newDrivers[0];
    }
    else if (changeClass?.driversHHMs?.householdMembers?.newMembers?.length > 0) {
        return changeClass?.driversHHMs?.householdMembers?.newMembers[0];
    }

    // Check if any stored drivers have a componentTracker with a value and return
    const driverWithTracker = changeClass?.driversHHMs?.drivers?.storedDrivers?.find(driver => driver?.componentTracker);
    if (driverWithTracker) {
        return driverWithTracker;
    }

    // Check if any stored household members have a componentTracker with a value and return
    const memberWithTracker = changeClass?.driversHHMs?.householdMembers?.storedMembers?.find(member => member?.componentTracker);
    if (memberWithTracker) {
        return memberWithTracker;
    }

    return {};
}

const formatDate = (dateString) => {
    const monthNames = {
        January: 1, February: 2, March: 3, April: 4, May: 5,
        June: 6, July: 7, August: 8, September: 9, October: 10,
        November: 11, December: 12
    };
    const [monthName, day, year] = dateString?.split(' ');
    const month = monthNames[monthName];

    if (!month) {
        return dateString?.replace(/\s+/g, '/');
    }

    const date = new Date(year, month - 1, day);
    if (isNaN(date?.getTime())) {
        return dateString?.replace(/\s+/g, '/');
    }

    const formattedMonth = String(month)?.padStart(2, '0');
    const formattedDay = String(date?.getDate())?.padStart(2, '0');
    const formattedYear = date?.getFullYear();
    return `${formattedMonth}/${formattedDay}/${formattedYear}`;
};

export const DriverHHMContextComp = ({ children }) => {
    const addressChangeContext = useContext(AddressChangeContext)
    const householdChangeContext = useContext(HouseholdChangeContext)
    const location = useLocation()
    const context = location.pathname.includes("address") ? addressChangeContext : householdChangeContext;
    const changeClass = location.pathname.includes("address") ? "addressChangeClass" : "householdChangeClass";
    const [formType, setFormType] = useState("");

    // Fields for driver/hhm
    const initialDriverHHM = {
        ...InitialDriverHHM(context)
    }
    
    /**
     * Initialize driverHHM state with a valid object of type InitialDriverHHMType.
     * @type {[InitialDriverHHMType, Function]} The state and setter for driverHHM
     */
    const [driverHHM, setDriverHHM] = useState(getSavedForm(context[changeClass])); // Retrieves saved form if exists
    const currResidenceBill = storageData.getItem("currResidenceBill")
    const policyStreetAddress = JSON.parse(currResidenceBill).address1
    const modal = useModal()

    // Retrieves Form Values
    const driverHHMCallback = (name, value, infoKey) => {
        var tempInfo = driverHHM

        if (infoKey !== "" && infoKey !== undefined) {
            // tempInfo[infoKey][name] = value;
            tempInfo = { ...tempInfo, [infoKey]: { ...(tempInfo?.[infoKey] || {}), [name]: value } }
        }
        else {
            tempInfo[name] = value;
        }

        // If any of the DOB fields change, recalculate the age
        if (name === 'dobMonth' || name === 'dobDay' || name === 'dobYear') {
            const { dobMonth, dobDay, dobYear } = driverHHM.userInfo;
            if (dobMonth && dobDay && dobYear) {
                const age = calculateAge(dobMonth, dobDay, dobYear);
                driverHHM.userInfo.driverAge = age;
            }
        }
        setDriverHHM({ ...driverHHM, ...tempInfo });
    }

    // Vehicles stored to driver's new address
    const vehiclesNewAddressCallback = (name, value, checked, infoKey = "vehicleInfo") => {
        var tempInfo = driverHHM;
        tempInfo[infoKey] = { ...tempInfo[infoKey], [name]: [...(tempInfo?.[infoKey]?.[name] ? tempInfo?.[infoKey]?.[name] : [])] };

        if (checked) {
            tempInfo[infoKey][name].push(value);
        }
        else {
            tempInfo[infoKey][name] = tempInfo[infoKey][name].filter((veh) => veh?.VIN !== value?.VIN);
        }

        setDriverHHM({ ...driverHHM, ...tempInfo });
    }

    // Calculates the age of the driver on changes of the dob fields
    const calculateAge = (dobMonth = driverHHM.userInfo.dobMonth, dobDay = driverHHM.userInfo.dobDay, dobYear = driverHHM.userInfo.dobYear) => {
        if (dobMonth === "" || dobDay === "" || dobYear === "") return 0;
        const today = new Date();
        const birthMonthIndex = monthNameToIndex(dobMonth);
        const birthDate = new Date(dobYear, birthMonthIndex, dobDay);
        let age = today.getFullYear() - dobYear;
        const monthDifference = today.getMonth() - birthDate.getMonth();

        // Adjust age if the birthday hasn't happened yet this year
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < dobDay)) {
            age--;
        }
        return age;
    };

    // Determines which form to display
    const setForm = (value, id) => {
        setFormType(value);

        if (value == "UpdateDriver") {
            /** @type{HHMStoredMembers} */
            context[changeClass].driversHHMs.drivers.storedDrivers[id]["componentTracker"] = "Update Driver";

            const member = context[changeClass]?.driversHHMs?.drivers.storedDrivers[id]
            
            var temp = { ...initialDriverHHM, ...driverHHM }
            var [firstName, middleInitial, lastName] = member.fullName.split(' ');
            if (!lastName) {
                lastName = middleInitial || lastName;
                middleInitial = '';
            }

            temp = {
                ...temp, userInfo: {
                    ...temp.userInfo,
                    driverAge: '', dobDay: member.daySelect, dobMonth: member.monthSelect, dobYear: member.yearSelect,
                    firstName, middleInitial, lastName,
                    gender: member.gender, maritalStatus: member.mStatus, relationshipToPolicyholder: member.currRel
                }, driverInfo:{
                    principalStatus: member.currStatus
                },
                componentTracker: 'Update Driver',
                // requestedEffDate: ""
            }
            console.log("TEMP:", temp);
            
            setDriverHHM(temp)

        }
        else if (value == "UpdateHHM") {
            /** @type{HHMStoredMembers} */
            const member = context[changeClass]?.driversHHMs?.householdMembers?.storedMembers[id]

            var temp = { ...initialDriverHHM, ...driverHHM }
            var [firstName, middleInitial, lastName] = member.fullName.split(' ');
            if (!lastName) {
                lastName = middleInitial || lastName;
                middleInitial = '';
            }

            temp = {
                ...temp, userInfo: {
                    ...temp.userInfo,
                    driverAge: '', dobDay: member.daySelect, dobMonth: member.monthSelect, dobYear: member.yearSelect,
                    firstName, middleInitial, lastName,
                    gender: member.gender, maritalStatus: member.mStatus, relationshipToPolicyholder: member.currRel
                },
                driverInfo:{
                    principalStatus: member.currStatus
                },
                componentTracker: 'Update HHM',
                // requestedEffDate: ""
            }
            console.log("TEMP:", temp);

            setDriverHHM(temp)
        }
        else {
            var tempInfo = initialDriverHHM;
            // tempInfo["requestedEffDate"] = ""
            tempInfo["componentTracker"] = "Add " + value
            setDriverHHM({ ...tempInfo })
        }
    }

    // Set the driver/hhm to empty for landing back on the household main page & clears the saved responses for the form
    const clearDriverHHM = () => {
        const originalDrivers = JSON?.parse(storageData.getItem("storedDrivers"));
        const originalMembers = JSON?.parse(storageData.getItem("storedHouseholdMembers"));
        const originalVehicles = JSON?.parse(storageData.getItem("storedVehicles"));
        context[changeClass].driversHHMs.drivers.storedDrivers = originalDrivers;
        context[changeClass].driversHHMs.householdMembers.storedMembers = originalMembers;
        context[changeClass].driversHHMs.drivers.newDrivers.length = 0
        context[changeClass].driversHHMs.householdMembers.newMembers.length = 0
        context[changeClass].driverAssignment.drivers = originalDrivers;
        context[changeClass].driverAssignment.vehicles = originalVehicles;

        if (changeClass === "addressChangeClass") {
            context?.setAddressChangeClass({ ...context[changeClass] })
            storageData.setItem("AddressChangeClass", JSON.stringify(context[changeClass]))
        }
        else if (changeClass === "householdChangeClass") {
            context?.setHouseholdChangeClass({ ...context[changeClass] })
            storageData.setItem("HouseholdChangeClass", JSON.stringify(context[changeClass]))
        }

        setDriverHHM({});
    }

    // Adds fields for Driver Assignment
    const addKeysToTempDriver = (tempDriverHHM) => {
        tempDriverHHM["fullName"] = tempDriverHHM?.userInfo?.firstName + " " + tempDriverHHM?.userInfo?.lastName;
        tempDriverHHM["licenseNum"] = tempDriverHHM?.driverInfo?.licenseNumber;
        tempDriverHHM["DOB"] = formatDate(tempDriverHHM?.userInfo?.dobMonth + " " + tempDriverHHM?.userInfo?.dobDay + " " + tempDriverHHM?.userInfo?.dobYear);
    }

    // Saves the driver/hhm to the appropriate class
    const SaveFormToClass = (changeClass, tempDriverHHM = driverHHM) => {
        var changeClassCopy = changeClass

        if (tempDriverHHM?.componentTracker === "Add Driver") {
            addKeysToTempDriver(tempDriverHHM);
            
            // Update to current new driver
            if (changeClassCopy?.driversHHMs?.drivers?.newDrivers.length > 0) {
                changeClassCopy.driversHHMs.drivers.newDrivers[0] = tempDriverHHM
            }
            else {
                changeClassCopy.driversHHMs.drivers.newDrivers.push(tempDriverHHM)
            }
        }
        else if (tempDriverHHM?.componentTracker === "Add Household Member") {
            addKeysToTempDriver(tempDriverHHM);

            // Update to current new hhm
            if (changeClassCopy?.driversHHMs?.householdMembers?.newMembers.length > 0) {
                changeClassCopy.driversHHMs.householdMembers.newMembers[0] = tempDriverHHM
            }
            else {
                changeClassCopy?.driversHHMs?.householdMembers?.newMembers?.push(tempDriverHHM)
            }
        }
        else if (tempDriverHHM?.componentTracker === "Update Driver") {
            const driverFullName = tempDriverHHM?.userInfo?.firstName + " " + tempDriverHHM?.userInfo?.lastName;
            const driverIndex = changeClassCopy?.driversHHMs?.drivers?.storedDrivers?.findIndex(driver => driver.fullName === driverFullName);
            const driverIndex2 = changeClassCopy?.driversHHMs?.drivers?.storedDrivers?.findIndex(driver => driver?.userInfo?.firstName + " " + driver?.userInfo?.lastName === driverFullName);
            const indexToReplace = driverIndex !== -1 ? driverIndex : driverIndex2;

            // If a matching driver is found, replace it
            if (indexToReplace !== -1) {
                changeClassCopy.driversHHMs.drivers.storedDrivers[indexToReplace] = tempDriverHHM;
            }
        }
        else if (tempDriverHHM?.componentTracker === "Update HHM") {
            addKeysToTempDriver(tempDriverHHM);

            const driverFullName = tempDriverHHM?.userInfo?.firstName + " " + tempDriverHHM?.userInfo?.lastName;
            const memberIndex = changeClassCopy?.driversHHMs?.householdMembers?.storedMembers?.findIndex(member => member.fullName === driverFullName);
            const memberIndex2 = changeClassCopy?.driversHHMs?.householdMembers?.storedMembers?.findIndex(member => member?.userInfo?.firstName + " " + member?.userInfo?.lastName === driverFullName);
            const indexToReplace = memberIndex !== -1 ? memberIndex : memberIndex2;

            // If a matching member is found, replace it
            if (indexToReplace !== -1) {
                changeClassCopy.driversHHMs.householdMembers.storedMembers[indexToReplace] = tempDriverHHM;
            }
        }
        return changeClassCopy;
    }

    return (
        <DriverHHMContext.Provider
            value={{
                driverHHM, setDriverHHM,
                driverHHMCallback,
                vehiclesNewAddressCallback,
                modal, policyStreetAddress,
                formType, setFormType,
                setForm, clearDriverHHM,
                calculateAge,
                SaveFormToClass,
                formatDate
            }}>
            {children}
        </DriverHHMContext.Provider>
    )
}

/**
 * Custom hook to get the driver HHM context.
 * @returns {DriverHHMContextType} The context value
 */
export const useDriverHHM = () => useContext(DriverHHMContext);

/** 
 * @typedef {import('./InitialDriverHHM').InitialDriverHHMType} InitialDriverHHMType
 */

/**
 * @typedef {Object} DriverHHMContextType
 * @property {InitialDriverHHMType} driverHHM - The driver information
 * @property {any} setDriverHHM - Function to set driverHHM
 * @property {any} driverHHMCallback - Callback function for driverHHM
 * @property {any} vehiclesNewAddressCallback - Callback function for new vehicle address
 * @property {any} modal - Modal component or state
 * @property {any} policyStreetAddress - Street address of the policyholder
 * @property {any} formType - Type of the form (could be string, number, etc.)
 * @property {any} setFormType - Function to set the form type
 * @property {any} setForm - Function to set the form state
 * @property {any} clearDriverHHM - Function to clear the driverHHM state
 * @property {any} SaveFormToClass - Function to save form to class
 * @property {any} calculateAge - Function to calculate age of user within form
 */

/**
* @typedef {Object} HHMStoredMembers
* @property {string} fullName - The full name of the person.
* @property {number} monthSelect - The selected birth month (1-12).
* @property {number} daySelect - The selected birth day (1-31).
* @property {number} yearSelect - The selected birth year.
* @property {string} DOB - The date of birth in MM/DD/YYYY format.
* @property {string} mStatus - The marital status of the person.
* @property {string} gender - The gender of the person.
* @property {string} licenseNum - The driver's license number.
* @property {string} currRel - The current relationship status (e.g., Self, Spouse, etc.).
* @property {string} driverStatus - The current driver status.
* @property {string} initialType - The initial type of the person (e.g., Driver).
* @property {string} currStatus - The current status of the person (e.g., Principal).
*/
